
import { SearchAndSelect } from '../../..';
import { Box, Chip, CircularProgress, InputAdornment, ListItemButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import { getTreatAllergies } from '@uplift/api';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';

const name = 'allergies';
const searchName = 'allergies-search';

type AutocompleteAllergyOption = {
  name: string;
  id: string;
};

const useStyles = makeStyles()(() => ({
  input: {
    '&::placeholder': {
      fontStyle: 'italic'
    }
  },
  chipRoot: {
    borderRadius: 'unset'
  },
}));

export const AllergiesForm: React.VFC = () => {
  const { classes } = useStyles();
  const [ field, _meta, helpers ] = useField<string[]>(name);
  const [ searchField, _searchMeta, searchHelpers ] = useField<string>(searchName);
  const text = useMemo(() => searchField.value ?? '', [ searchField.value ]);

  const [ suggestions, setSuggestions ] = useState<AutocompleteAllergyOption[]>([]);
  const [ loading, setLoading ] = useState(false);

  const fetchSuggestions = useCallback(async (text: string) => {
    const res = await getTreatAllergies(text);

    return res.data.map((allergy): AutocompleteAllergyOption => ({
      name: allergy.name,
      id: allergy.id
    }));
  }, []);

  const debouncedFetch = useMemo(() => debounce((text: string) => {
    fetchSuggestions(text).then((suggestions) => {
      setSuggestions(suggestions);
    }).catch(() => {
      setSuggestions([]);
    }).finally(() => {
      setLoading(false);
    });
  }, 300), [ fetchSuggestions ]);

  useEffect(() => {
    if (text.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncedFetch(text);
  }, [ text, debouncedFetch ]);

  const onAddValue = (allergyNameToAdd: string) => {
    helpers.setValue([ ...field.value, allergyNameToAdd ]);
    searchHelpers.setValue('');
  };

  const onDeleteValue = (allergyNameToDelete: string) => {
    helpers.setValue(field.value.filter(allergyName => allergyName !== allergyNameToDelete));
  };

  return (
    <Box>
      <Box>
        {field.value.map((allegyName) => {

          return (
            <Box mb={2} key={allegyName}>
              <Chip
                label={allegyName}
                variant="outlined"
                classes={{ root: classes.chipRoot }}
                onDelete={() => onDeleteValue(allegyName)}
              />
            </Box>
          );
        })}
      </Box>
      <SearchAndSelect
        color="secondary"
        fullWidth
        id={name}
        name={searchName}
        label="Add Allergies"
        options={suggestions}
        popperConstraint={suggestions.length !== 0 && !loading}
        listProps={{ disablePadding: true }}
        inputClasses={{ input: classes.input }}
        renderListItem={(option) => (
          <ListItemButton onClick={() => onAddValue(option.name)}>
            {option.name}
          </ListItemButton>
        )}
        placeholder="Search allergies"
        startAdornment={<InputAdornment position="start"><Search /></InputAdornment>}
        endAdornment={loading && <InputAdornment position="end"><CircularProgress color="secondary" size="24px" /></InputAdornment>}
      />
    </Box>
  );
};
